/**
 * Tabs — an ARIA tablist for in-page section switching, styled to match the AppShell's top nav so
 * the two read as one system (underline on the active item, 44px targets, ink/ink-70 states).
 *
 * WAI-ARIA tabs pattern, roving tabindex: exactly one tab is in the tab sequence (`tabIndex 0`),
 * the rest are reachable only by arrow keys (`tabIndex -1`), so a keyboard user tabs past the whole
 * strip in one step and steers within it with the arrows. Home/End jump to the ends. Activation is
 * automatic (moving selects), which is the right call here — each panel is a cheap list, so there
 * is no expensive side effect to defer behind a second keypress.
 *
 * The parent owns the single panel and its `id` (`panelId`); every tab points its `aria-controls`
 * at that one id, and the parent labels the panel with the active tab's id (`tab-${value}`). Only
 * the active panel is ever in the DOM, so there are no dangling `aria-controls` references — the
 * one id a tab controls always resolves.
 */
import { useRef, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';

export interface TabItem {
  id: string;
  label: string;
}

interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** Names the tablist for assistive tech. */
  label: string;
  /** The id of the single panel these tabs control — every tab's `aria-controls`. */
  panelId: string;
}

export function Tabs({ items, value, onChange, label, panelId }: TabsProps) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = items.findIndex((item) => item.id === value);
    if (index < 0) return;

    let next: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % items.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextId = items[next].id;
    onChange(nextId);
    refs.current[nextId]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="-mb-px flex flex-wrap gap-1 overflow-x-auto"
    >
      {items.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            ref={(node) => {
              refs.current[item.id] = node;
            }}
            type="button"
            role="tab"
            id={`tab-${item.id}`}
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.id)}
            className={cn(
              'inline-flex min-h-[44px] items-center whitespace-nowrap border-b-2 px-3 font-display text-[13px] font-medium transition-colors duration-ui ease-thread focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
              selected
                ? 'border-sage-deep text-ink'
                : 'border-transparent text-ink-70 hover:text-ink',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
